<template>
  <VList density="compact" nav>
    <template v-if="isRailModelValue">
      <div v-for="(item, index) in sideBarList" :key="`side-${index}`">
        <VListItem
          v-if="!('children' in item)"
          :title="item.title"
          :prependIcon="resolveSidebarIcon(item)"
          :value="item.value"
          activeClass="active-item"
          :to="item.link"
        >
          <VTooltip activator="parent" location="start">
            {{ item.title }}
          </VTooltip>
        </VListItem>

        <VListItem
          v-else
          :title="item.title"
          :prependIcon="resolveSidebarIcon(item)"
          :value="item.value"
          activeClass="active-item"
          :to="item.link"
          @click="toggleTooltip(index)"
        >
          <VTooltip
            v-model="tooltips[index]"
            activator="parent"
            location="start"
          >
            <template #default>
              <VListItem
                v-for="(child, _index) in item.children"
                :key="`child-${_index}`"
                :title="child.title"
                :value="child.value"
                :to="child.link"
                :active="isChildLinkActive(child.link)"
                class="text-start ms-2"
              >
                <template #prepend>
                  <VIcon icon="dot" size=".5rem" />
                </template>
              </VListItem>
            </template>
          </VTooltip>
        </VListItem>
      </div>
    </template>

    <template v-else>
      <div v-for="(item, index) in sideBarList" :key="`side-${index}`">
        <VListItem
          v-if="!('children' in item) || isRailModelValue"
          :title="item.title"
          :prependIcon="resolveSidebarIcon(item)"
          :value="item.value"
          :to="item.link"
        />

        <VListGroup v-else :value="item.value" :isOpen="!isRailModelValue">
          <template #activator="{ props, isOpen }">
            <VListItem
              v-bind="props"
              :prependIcon="resolveSidebarIcon(item)"
              :title="item.title"
              :activeClass="isOpen ? 'active-item' : ''"
              :elevation="0"
            />
          </template>

          <div class="border-s ms-4">
            <VListItem
              v-for="(child, _index) in item.children"
              :key="`child-${_index}`"
              :title="child.title"
              :value="child.value"
              :to="child.link"
              :active="isChildLinkActive(child.link)"
              class="text-start ms-2"
            >
              <template #prepend>
                <VIcon icon="dot" size=".5rem" />
              </template>
            </VListItem>
          </div>
        </VListGroup>
      </div>
    </template>
  </VList>
</template>

<script lang="ts" setup>
import {
  VIcon,
  VList,
  VListGroup,
  VListItem,
  VTooltip,
} from 'vuetify/components'
import { ref } from 'vue'
import { useRoute } from 'vue-router'
import type { RouteLocationRaw } from 'vue-router'
import type { Props, Emits } from './TheSideBarList.types'
import type { SideBarItem } from '@/modules/Core/types/model/sidebar'
import { useSideBar } from '@/modules/Core/composables'

defineProps<Props>()
defineEmits<Emits>()

const isRailModelValue = defineModel<boolean>('rail')

const route = useRoute()
const { sideBarList } = useSideBar()

const tooltips = ref<Record<number, boolean>>({})

function isNavLinkActive(link: string): boolean {
  if (link === '/') {
    return route.path === '/'
  }

  return route.path === link || route.path.startsWith(`${link}/`)
}

function isSidebarItemActive(item: SideBarItem): boolean {
  if (item.link) {
    return isNavLinkActive(item.link)
  }

  if (item.children?.length) {
    return item.children.some((child) => isChildLinkActive(child.link))
  }

  return false
}

function resolveSidebarIcon(item: SideBarItem): string {
  return isSidebarItemActive(item) ? item.activeIcon : item.icon
}

function isChildLinkActive(link?: RouteLocationRaw) {
  if (!link) {
    return false
  }

  if (typeof link === 'string') {
    if (link === '/recruitment/employment-form') {
      return route.path.startsWith('/recruitment/employment-form')
    }

    return route.path === link
  }

  if ('name' in link && link.name) {
    return route.name === link.name
  }

  if ('path' in link && typeof link.path === 'string') {
    return route.path === link.path
  }

  return false
}

function toggleTooltip(index: number) {
  tooltips.value[index] = !tooltips.value[index]
}
</script>

<style scoped>
:deep(.v-list-item--active) {
  .v-list-item__prepend,
  .v-list-item-title {
    color: rgb(var(--v-theme-primary)) !important;
  }
}

.active-item {
  background-color: rgba(var(--v-theme-gray-600), 0.08) !important;
}
.v-list-group__items .v-list-item {
  padding-inline-start: 1.2rem !important;
}

:deep(.v-list-item__content) {
  margin: 0 10px 0 0 !important;
}
</style>
